import { useQuery } from '@tanstack/react-query';

import { createEntityMutations } from '../../../shared/api/createEntityMutations.js';
import { useCurrentScope } from '../../../shared/scope/useCurrentScope.js';

import {
  createTemplate,
  deleteTemplate,
  getTemplatesQueryKey,
  listTemplates,
  updateTemplate,
  type CreateTemplateInput,
  type ListTemplatesFilters,
  type TemplateItem,
  type UpdateTemplateInput,
} from '../api/templates.client.js';

export function useTemplates(filters: ListTemplatesFilters = {}) {
  const scope = useCurrentScope();
  const workspaceId = scope?.workspaceId ?? 'anonymous';

  return useQuery({
    queryKey: getTemplatesQueryKey(workspaceId, filters),
    queryFn: () => listTemplates(filters),
    enabled: scope !== null,
  });
}

const templateMutations = createEntityMutations<
  TemplateItem,
  CreateTemplateInput,
  UpdateTemplateInput
>({
  key: 'templates',
  create: createTemplate,
  update: updateTemplate,
  remove: deleteTemplate,
});

export const useCreateTemplateMutation = templateMutations.useCreate;
export const useDeleteTemplateMutation = templateMutations.useDelete;

/**
 * У шаблонов исторически другое имя параметра (`templateId`), и на нём завязан
 * боковой редактор. Оставляем обёртку, чтобы не переписывать вызовы ради переименования.
 */
export function useUpdateTemplateMutation() {
  const mutation = templateMutations.useUpdate();
  return {
    ...mutation,
    mutate: (vars: { templateId: string; input: UpdateTemplateInput }) =>
      mutation.mutate({ id: vars.templateId, input: vars.input }),
    mutateAsync: (vars: { templateId: string; input: UpdateTemplateInput }) =>
      mutation.mutateAsync({ id: vars.templateId, input: vars.input }),
  };
}
